import moment from 'moment';
import { mixed, object, string } from 'yup';

import { toMonthStart } from '../utils';
import { MEDICATION_TEXT_MAX_LENGTH } from './constants';
import { API_DATE_FORMAT } from '../../../constants';

import type { Moment } from 'moment';
import type {
  IMedication,
  MedicationFieldName,
  MedicationFormLimits,
  MedicationFormValues,
  MedicationPayload,
} from './types';

export function toMedicationPayload(values: MedicationFormValues): MedicationPayload {
  return {
    medication_name: values.medication_name,
    dosage: values.dosage,
    start_date: toMonthStart(values.start_date)?.format(API_DATE_FORMAT) ?? null,
    physician_name: values.physician_name,
    reason: values.reason,
  };
}

export function toMedicationFieldPayload<K extends MedicationFieldName>(
  name: K,
  values: MedicationFormValues,
): MedicationPayload {
  const data: MedicationPayload = {};
  data[name] = toMedicationPayload(values)[name];
  return data;
}

export function mapMedicationRowToValues(row: IMedication): MedicationFormValues {
  return {
    medication_name: row.medication_name ?? '',
    dosage: row.dosage ?? '',
    start_date: toMonthStart(row.start_date ? moment(row.start_date) : null),
    physician_name: row.physician_name ?? '',
    reason: row.reason ?? '',
  };
}

export function getMedicationRowLabel(row: IMedication): string {
  return `${row.medication_name ?? ''} - ${row.dosage ?? ''}`;
}

export function buildMedicationFormSchema(limits: MedicationFormLimits) {
  return object({
    medication_name: medicationTextField('Medication', limits.medicationNameMinLength),
    dosage: medicationTextField('Dosage', limits.dosageMinLength),
    start_date: mixed<Moment>().nullable().required(),
    physician_name: medicationTextField('Prescribing physician', limits.physicianNameMinLength),
    reason: medicationTextField('Reason', limits.reasonMinLength),
  });
}

function medicationTextField(label: string, minLength?: number) {
  const field = string().trim().max(MEDICATION_TEXT_MAX_LENGTH).required();
  if (minLength === undefined) return field;
  return field.min(minLength, `${label} must be at least ${minLength} characters`);
}
